import BaseFormGroup from 'components/atoms/base-form/group';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import BaseModal from 'components/atoms/base-modal';
import { useClinicCareManagersSelectBox } from 'infrastructure/hooks/clinic/use-clinic-care-managers-select-box';
import useUser from 'utils/useUser';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import { arrUniqEl } from 'infrastructure/functions';

import s from './styles.module.scss';
import { CareManagersDropdown } from './dropdown';
import CareManagersEditTable from './table';

import type { IBaseSelectBoxOption } from 'components/atoms/base-select-box/types';
import type {
  IClinicCareManagerLite,
  IClinicCreateCareManager,
} from 'infrastructure/interfaces';
import type { Clinic } from 'infrastructure/classes/clinic';
import type {
  IIndependentFormProps,
  IIndependentFormRef,
} from 'infrastructure/interfaces/common/i-independent-form';

interface IProps extends IIndependentFormProps {
  data?: Clinic;
  isEditMode?: boolean;
  agencyGuid?: string | null;
}

interface IControlsProps {
  onClick: () => void;
}

const FormDisplayName = 'ClinicCareManagersForm';

const Controls: React.FC<IControlsProps> = (props) => {
  const { onClick } = props;
  const { t } = useTranslation();
  return (
    <BaseButton
      dataCy="editClinicCareManagers-button"
      width="160px"
      onClick={onClick}
      label={t('controls.edit')}
    />
  );
};

const ClinicCareManagersForm = forwardRef<
  IIndependentFormRef<IClinicCreateCareManager>,
  IProps
>((props, ref) => {
  const { data, isEditMode, agencyGuid } = props;

  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const initializedRef = useRef(false);

  const user = useUser();
  const doctorsGuids = useRef<
    Map<string, IBaseSelectBoxOption & { needDelete: boolean }>
  >(new Map());
  const [tableCareManagers, setTableCareManagers] = useState<
    IClinicCareManagerLite[]
  >([]);

  const { careManagers, refetch, loading, mutateAsync, mutateLoading } =
    useClinicCareManagersSelectBox({
      clinicGuid: data?.guid,
      page: 1,
      items: 1000,
    });

  const canEdit = isEditMode && user.isAgencyDoctor && !mutateLoading;

  const submitForm = async () => {
    const addDoctorsGuids = [...doctorsGuids.current]
      .filter(
        ([key, { needDelete }]) =>
          !needDelete && !careManagers.some((el) => el.doctorGuid === key),
      )
      .map(([key]) => key);
    if (isEditMode) {
      if (user.isAgencyDoctor) {
        const deleteDoctorsGuids = [...doctorsGuids.current]
          .filter(([_, { needDelete }]) => needDelete)
          .map(([key]) => key);
        await mutateAsync({
          addDoctorsGuids,
          deleteDoctorsGuids,
        });
      }
    }
    return {
      values: { careManagers: addDoctorsGuids },
      formName: FormDisplayName,
      isValid: true,
    };
  };

  useImperativeHandle(ref, () => ({
    submit: submitForm,
    reset: () => {
      refetch();
      doctorsGuids.current.clear();
    },
  }));

  const handleUpdate = (
    val: string,
    option: IBaseSelectBoxOption,
    needDelete: boolean,
  ) => {
    doctorsGuids.current.set(val, { ...option, needDelete });
  };

  const onEdit = () => setOpen(true);
  const onCancel = () => setOpen(false);

  const onOk = () => {
    const filtered = careManagers.filter((el) => {
      const item = doctorsGuids.current.get(el.doctorGuid);
      return !item?.needDelete;
    });
    const mapped = [...doctorsGuids.current]
      .filter(([_, el]) => !el.needDelete)
      .map(([_, cm]) => {
        const labelStr = typeof cm.label === 'string' ? cm.label : '';
        const [firstName, lastName] = labelStr.split(' ');
        return {
          firstName: firstName || '',
          lastName: lastName || '',
          doctorGuid: cm.value,
        };
      });

    setTableCareManagers(arrUniqEl([...filtered, ...mapped], 'doctorGuid'));
    setOpen(false);
  };

  useEffect(() => {
    if (initializedRef.current) return;
    if (!careManagers.length) return;

    setTableCareManagers(careManagers);
    initializedRef.current = true;
  }, [careManagers]);

  return (
    <BaseSpinWrapper spinning={loading}>
      <BaseFormGroup
        controls={canEdit ? <Controls onClick={onEdit} /> : undefined}
        label={t('labels.ccmSpecialists')}
      >
        {isEditMode ? (
          <>
            <CareManagersEditTable items={tableCareManagers} />
            <BaseModal
              title={`${t('labels.select')} ${t('labels.ccmSpecialists')}`}
              open={open}
              onCancel={onCancel}
              width="1000px"
              outsideClose={false}
            >
              <CareManagersDropdown
                defaultValue={
                  tableCareManagers.map((el) => ({
                    value: el.doctorGuid,
                    label: `${el.firstName} ${el.lastName}`,
                  })) || []
                }
                onSelect={(val, option) => handleUpdate(val, option, false)}
                onDeselect={(val, option) => handleUpdate(val, option, true)}
                agencyGuid={agencyGuid || ''}
              />
              <div className={s['care-managers-submit-wrapper']}>
                <BaseButton
                  dataCy="save-care-managers"
                  label={t('controls.save')}
                  onClick={onOk}
                />
              </div>
            </BaseModal>
          </>
        ) : (
          <CareManagersDropdown
            defaultValue={[]}
            onSelect={(val, option) => handleUpdate(val, option, false)}
            onDeselect={(val, option) => handleUpdate(val, option, true)}
            agencyGuid={agencyGuid || ''}
          />
        )}
      </BaseFormGroup>
    </BaseSpinWrapper>
  );
});

export default ClinicCareManagersForm;
